<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch, type PropType, type Ref } from 'vue'
import {
  deleteMemorialArticle,
  type DeleteMemorialArticleResponse,
  type MemorialArticleDTO,
  type MemorialPrivilegeDTO,
  type LifeDTO,
  setArticleTop,
  setObituaryTop,
  type ArticleWrapper,
  setObituaryHide
} from '@/composables/life/life'
import { isNotEmpty, isEmpty, removeHTMLTags, renderMessage, useSsrSafeIsMobile } from '@/utils/util'
import { useRouter, useRoute } from 'vue-router'
import { closeLoading, openLoading } from '../../loading/loading'
import type { CommonResponse } from '@/apis/api'
import { handleErr } from '@/composables/exception/handler'
import { CODE_OK } from '@/apis/code'
import { draftStore } from '@/composables/memorize/draft'
import { useI18n } from 'vue-i18n'
import RToolTip from '@/components/base/RToolTip/RToolTip.vue'
import { ElMessageBox, ElMessage } from 'element-plus'
import { publishArticle } from '@/composables/article/article'
import { uploadFile } from '@/composables/media/media'
import ImageCropperPage from '@/pages/tools/ImageCropperPage.vue'
import {
  GenerateObiMsgCommand,
  obituaryPreview,
  familyMember,
  userOptions,
  autoEditForm,
  showImage
} from '@/composables/obituary/obituary'
import { getCurrentUser } from '@/composables/user/users'
import { isSetRealName } from '@/composables/user/users'
import SetRealNameDialog from '@/components/business/me/SetRealNameDialog.vue'
import RInput from '@/components/base/RInput/RInput.vue'
const realNameModalRef = ref<InstanceType<typeof SetRealNameDialog> | null>(null)
const router = useRouter()
const { t } = useI18n()

const emit = defineEmits(['changed'])

const model = defineModel<boolean>()

const showArticleDrawer = ref(false)

const props = defineProps({
  privilege: {
    type: Object as PropType<MemorialPrivilegeDTO | null>,
    default: (): MemorialPrivilegeDTO | null => null
  },
  articleList: {
    type: Array as PropType<ArticleWrapper[]>,
    default: (): ArticleWrapper[] => []
  },
  life: {
    type: Object as PropType<LifeDTO | null>,
    default: (): LifeDTO | null => null
  },
  serviceId: {
    type: String,
    default: ''
  },
  creatorId: {
    type: String,
    default: ''
  },
  largeMode: {
    type: Boolean,
    default: false
  }
})

onMounted(() => {
  console.log('privilege', props.articleList)
})

const isObituaryHidden = ref(!!props.articleList.find((item: any) => item.obituaryDTO && item.hide))

const serviceId = computed(() => {
  console.log('收到的serviceId.value: ', props.serviceId)
  return props.serviceId
})

/*
  文章排序规则：
  1. 置顶文章一定是【0】，（top=true）
  2. 如果都是置顶或都不是置顶，按时间倒序，越旧越靠前
*/
const articleList: Ref<Array<ArticleWrapper>> = computed(() => {
  return [...props.articleList.filter((item: any) => !item.hide)].sort((a, b) => {
    if (a.top !== b.top) return a.top ? -1 : 1
    return a.createdAt - b.createdAt
  })
})

const figureSummary = computed(() => {
  const raw = (props.life?.figureSummary || '').trim()
  if (!raw) return ''
  if (/[。.!！]$/.test(raw)) return raw
  const end = /[\u4e00-\u9fff]/.test(raw) ? '。' : '.'
  return raw + end
})

const fullName = computed(() => {
  return props.life?.name?.fullName || ''
})

const privilege = computed(() => {
  console.log('privilege！！！！！！', props.privilege)
  return props.privilege
})

// 改为数组，每篇文章一个引用和状态
const moreRefs = ref<any[]>([])
let newArticleRef = ref(null)
const moreVisibles = ref<boolean[]>([])
let newArticle = ref(false)

const canArticle = computed(() => {
  const permission = privilege.value?.permissions.find((item) => item.name === 'm_article')
  console.log('comment permission:', permission, permission?.value && permission.value === 'MANAGE')
  return permission?.value && permission.value !== 'READ'
})

const canDelete = computed(() => {
  const permission = privilege.value?.permissions.find((item) => item.name === 'm_article')
  console.log('comment permission:', permission)
  return permission?.value && permission.value === 'MANAGE'
})

// 控制创建和编辑讣告，只有纪念创建者可以创建和编辑讣告，只有当
const canCreateObituary = computed(() => {
  return (
    props.articleList.findIndex((item: ArticleWrapper) => item.obituaryDTO) === -1 &&
    props.creatorId === getCurrentUser().value?.user?.id
  )
})
const canEditObituary = computed(() => {
  console.log('canEditObituary', props.creatorId === getCurrentUser().value?.user?.id)
  return props.creatorId === getCurrentUser().value?.user?.id
})
const canObituaryTop = computed(() => {
  const permission = privilege.value?.permissions.find((item) => item.name === 'm_article')
  console.log('obituary permission:', permission)
  return permission?.value && permission.value === 'MANAGE'
})

const contributeDialogVisible = ref(false)
const contributeMemorialArticleId = ref('')
const contributeAuthor = ref('')
const contributeTitle = ref('')
const contributeIntro = ref('')
const contributeThumbUrl = ref('')
const contributeSubmitting = ref(false)
const contributeThankVisible = ref(false)

const thumbPickerVisible = ref(false)
const thumbCropVisible = ref(false)
const thumbCropPath = ref('')
const thumbFileInputRef = ref<HTMLInputElement | null>(null)
let thumbObjectUrl: string | null = null

type PresetThumb = {
  imgUrl: string
  name: string
}

function triggerThumbUpload() {
  thumbFileInputRef.value?.click()
}

function onThumbFileSelected(event: Event) {
  const input = event.target as HTMLInputElement
  const files = input.files
  if (!files || files.length === 0) return
  const file = files[0]
  input.value = ''
  if (!/^image\/\w+/.test(file.type)) {
    ElMessage.warning({
      message: renderMessage(t('exception.file.wrong_type'))
    })
    return
  }
  if (thumbObjectUrl) URL.revokeObjectURL(thumbObjectUrl)
  thumbObjectUrl = URL.createObjectURL(file)
  thumbCropPath.value = thumbObjectUrl
  thumbCropVisible.value = true
}

function removeContributeThumb() {
  contributeThumbUrl.value = ''
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), type, quality)
  })
}

async function resizeToSquare(blob: Blob, size: number) {
  if (!import.meta.client) return blob
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return blob

  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob)
    ctx.drawImage(bitmap, 0, 0, size, size)
    bitmap.close?.()
    return await canvasToBlob(canvas, 'image/png', 0.92)
  }

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error('read failed'))
    reader.readAsDataURL(blob)
  })
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const im = new Image()
    im.onload = () => resolve(im)
    im.onerror = () => reject(new Error('image load failed'))
    im.src = dataUrl
  })
  ctx.drawImage(img, 0, 0, size, size)
  return await canvasToBlob(canvas, 'image/png', 0.92)
}

async function onThumbCropResult(blob: Blob) {
  try {
    openLoading(t('alt.uploading'))
    const resized = await resizeToSquare(blob, 512)
    const fileName = `article_thumb_${Date.now()}.png`
    const { data, error, statusCode } = await uploadFile(resized, fileName)
    if (error.value) {
      handleErr(data, error, statusCode)
      return
    }
    if (data.value?.code === CODE_OK) {
      const file = data.value.data.file
      contributeThumbUrl.value = file.middleQuality || file.url
      return
    }
    ElMessage({
      message: renderMessage(data.value?.message || 'Error'),
      type: 'warning'
    })
  } finally {
    closeLoading()
    if (thumbObjectUrl) {
      URL.revokeObjectURL(thumbObjectUrl)
      thumbObjectUrl = null
    }
  }
}

watch(
  () => thumbCropVisible.value,
  (v) => {
    if (v) return
    thumbCropPath.value = ''
    if (thumbObjectUrl) {
      URL.revokeObjectURL(thumbObjectUrl)
      thumbObjectUrl = null
    }
  }
)

function shouldShowContributeMenuItem(article: MemorialArticleDTO | null | undefined, count: number) {
  if (!article) return false
  const charNumber = article.charNumber || 0
  if (charNumber > count) return true
  const content = article.content || ''
  return removeHTMLTags(content).length > count
}

function openThumbPicker() {
  thumbPickerVisible.value = true
}

function onPresetThumbClick(url: string) {
  contributeThumbUrl.value = url
  thumbPickerVisible.value = false
}

function openContributeDialog(article: MemorialArticleDTO, index: number) {
  if (article.publishedArticleId) {
    ElMessage({
      message: renderMessage(t('article.contributed')),
      type: 'success'
    })
    return
  }

  if (!shouldShowContributeMenuItem(article, 200)) {
    ElMessage({
      message: renderMessage(t('article.at_least_chars', { count: 200 })),
      type: 'warning'
    })
    return
  }

  onCloseMore(index)
  contributeMemorialArticleId.value = article.id
  contributeAuthor.value = getCurrentUser().value?.user?.name?.fullName || ''
  contributeTitle.value = article.title || ''
  contributeIntro.value = ''
  contributeThumbUrl.value = ''
  contributeDialogVisible.value = true
}

async function submitContribute() {
  if (contributeSubmitting.value) return
  const author = contributeAuthor.value.trim()
  if (!author) {
    ElMessage({
      message: renderMessage(t('article.contribute_required_author')),
      type: 'warning'
    })
    return
  }
  const title = contributeTitle.value.trim()
  if (!title) {
    ElMessage({
      message: renderMessage(t('article.contribute_required_title')),
      type: 'warning'
    })
    return
  }
  const intro = contributeIntro.value.trim()
  if (!intro) {
    ElMessage({
      message: renderMessage(t('article.contribute_required_intro')),
      type: 'warning'
    })
    return
  }
  if (intro.length < 10) {
    ElMessage({
      message: renderMessage(t('article.contribute_required_intro_min', { count: 10 })),
      type: 'warning'
    })
    return
  }
  const imageUrl = contributeThumbUrl.value
  if (!imageUrl) {
    ElMessage({
      message: renderMessage(t('article.contribute_required_thumbnail')),
      type: 'warning'
    })
    return
  }
  if (!isSetRealName.value) {
    const isSuccess = await realNameModalRef.value?.open()
    if (!isSuccess) return
  }
  contributeSubmitting.value = true
  openLoading(t('create_m_article.uploading'))
  const { data, error, statusCode } = await publishArticle({
    memorialArticleId: contributeMemorialArticleId.value,
    author,
    title: title,
    shareContent: intro,
    imageUrl
  })
  closeLoading()
  contributeSubmitting.value = false
  if (error.value) {
    handleErr(data, error, statusCode)
    return
  }
  const resData = data as unknown as Ref<CommonResponse<any>>
  if (resData.value.code === CODE_OK) {
    contributeDialogVisible.value = false
    emit('changed')
    contributeThankVisible.value = true
  } else {
    ElMessage({
      message: renderMessage(resData.value.message),
      type: 'warning'
    })
  }
}

// 初始化moreVisibles数组
watch(
  () => articleList.value?.length,
  (newLength) => {
    if (newLength) {
      moreVisibles.value = new Array(newLength).fill(false)
    }
  },
  { immediate: true }
)

async function editArticle(index: number) {
  onCloseMore(index)
  if (!isSetRealName.value) {
    const isSuccess = await realNameModalRef.value?.open()

    if (!isSuccess) {
      console.log('用户取消了实名认证，停止后续操作')
      return
    }
  }
  if (articleList.value[index]?.memorialArticleDTO) {
    router.push(
      '/createMArticle?serviceId=' +
        props.privilege?.serviceId +
        '&isEdit=true&articleId=' +
        articleList.value[index]?.memorialArticleDTO?.id
    )
  } else {
    editObituary(index)
  }
}

function onCloseMore(index: number) {
  if (moreVisibles.value[index] !== undefined) {
    moreVisibles.value[index] = false
  }
  moreVisible.value = false
}

function toggleMore(index: number) {
  if (moreVisibles.value[index] !== undefined) {
    moreVisibles.value[index] = !moreVisibles.value[index]
  }
}

function handleArticleDelete(index: number) {
  onCloseMore(index)

  ElMessageBox.confirm(t('m.confirm_delete_title'), {
    confirmButtonText: t('action.confirm'),
    cancelButtonText: t('action.cancel'),
    type: 'warning'
  })
    .then(() => {
      deleteArticle(index)
      showArticleDrawer.value = false
    })
    .catch(() => {})
}

async function deleteArticle(index: number) {
  if (articleList.value[index]) {
    openLoading(t('alt.deleting'))
    const { data, error, statusCode } = await deleteMemorialArticle(articleList.value[index]?.memorialArticleDTO?.id)
    closeLoading()
    if (error.value) {
      handleErr(data, error, statusCode)
    } else {
      const resData = data as unknown as Ref<CommonResponse<DeleteMemorialArticleResponse>>
      if (resData.value.code === CODE_OK) {
        // success
        nextTick(() => {
          // clear draft
          draftStore.value.deleteMArticle(privilege.value.serviceId)
          emit('changed')
          // 返回
          onClose()
        })
        ElMessage({
          message: renderMessage(t('alt.delete_success')),
          type: 'success'
        })
      } else {
        ElMessage({
          message: renderMessage(resData.value.message),
          type: 'warning'
        })
      }
    }
  }
}

function onClose() {
  console.log('=== onClose')
  model.value = false
}

async function handleMemorialArticlePin(id: string) {
  const memorialArticle = articleList.value.find((article) => article.memorialArticleDTO?.id === id)
  const index = articleList.value.findIndex((item) => item.memorialArticleDTO?.id === id)
  if (index !== -1) {
    onCloseMore(index)
  }
  if (!memorialArticle?.memorialArticleDTO) return
  const { data, error, statusCode } = await setArticleTop(
    memorialArticle.memorialArticleDTO.id,
    !memorialArticle.memorialArticleDTO.top
  )
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<string>>
    if (resData.value.code !== CODE_OK) {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'error'
      })
    } else {
      emit('changed')
    }
  }
  if (showArticleDrawer.value) {
    setTimeout(() => {
      showArticleDrawer.value = false
    }, 200)
  }
}

async function handleObituaryPin(id: string) {
  const obituary = articleList.value.find((article) => article.obituaryDTO && article.obituaryDTO.id === id)
  const index = articleList.value.findIndex((item) => item.obituaryDTO && item.obituaryDTO.id === id)
  if (index !== -1) {
    onCloseMore(index)
  }
  const { data, error, statusCode } = await setObituaryTop(obituary.obituaryDTO.id, !obituary.obituaryDTO.top)
  if (error.value) {
    handleErr(data, error, statusCode)
  } else {
    const resData = data as unknown as Ref<CommonResponse<string>>
    if (resData.value.code !== CODE_OK) {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'error'
      })
    } else {
      emit('changed')
    }
  }
}

const currentArticle = ref(null)

function onArticleClick(index: number) {
  if (props.largeMode) {
    return
  }
  showArticleDrawer.value = true
  console.log('articleList', articleList.value, index)
  currentArticle.value = articleList.value[index].memorialArticleDTO
  currentArticleIndex.value = index
}

const onObituaryClick = (index: number) => {
  if (
    privilege.value === null ||
    getCurrentUser().value?.user?.id === articleList.value[index].obituaryDTO.creator.id
  ) {
    router.push('/obituary/' + articleList.value[index].obituaryDTO.id + '?redirect=true')
  } else {
    router.push('/obituary/' + articleList.value[index].obituaryDTO.id + '?redirect=true&isInvitee=true')
  }
}
const currentArticleRef = ref(null)
const currentArticleIndex = ref<number>(0)
const moreVisible = ref(false)

function createArticle() {
  router.push('/createMArticle?serviceId=' + serviceId.value + '&isEdit=false')
}
// TODO: 发送请求,修改讣告是否隐藏，并更新文章列表

async function hideObituary(index?: number) {
  console.log('hideObituary', articleList.value[index], index)
  isHiddenVisible.value = !isHiddenVisible.value

  if (index !== undefined) {
    const obituaryId = articleList.value[index].obituaryDTO.id
    let hide = articleList.value[index].obituaryDTO.hide
    if (articleList.value[index].obituaryDTO.top) {
      handleObituaryPin(obituaryId)
    }
    const { data, error, statusCode } = await setObituaryHide({ obituaryId, hide: !hide })
    if (error.value) {
      handleErr(data, error, statusCode)
    } else {
      const resData = data as unknown as Ref<CommonResponse<string>>
      if (resData.value.code !== CODE_OK) {
        ElMessage({
          message: renderMessage(resData.value.message),
          type: 'error'
        })
      } else {
        isObituaryHidden.value = !hide
        emit('changed')
      }
    }
  } else {
    const obituary = props.articleList.find((item: ArticleWrapper) => item.obituaryDTO)
    console.log('props.articleList', obituary)
    if (obituary) {
      const { data, error, statusCode } = await setObituaryHide({ obituaryId: obituary.obituaryDTO.id, hide: false })
      if (error.value) {
        handleErr(data, error, statusCode)
      } else {
        const resData = data as unknown as Ref<CommonResponse<string>>
        if (resData.value.code !== CODE_OK) {
          ElMessage({
            message: renderMessage(resData.value.message),
            type: 'error'
          })
        } else {
          isObituaryHidden.value = false
          emit('changed')
        }
      }
    }
  }
}

//
function handleCreateArticle() {
  if (canCreateObituary.value && canDelete.value) {
    newArticle.value = true
  } else {
    createArticle()
  }
}

const isHiddenRef = ref(null)
const isHiddenVisible = ref(false)

const editObituary = async (index: number) => {
  onCloseMore(index)
  const obituary = articleList.value[index].obituaryDTO
  await autoEditForm(obituary.id)
  console.log(obituary)
}

const ssrSafeIsMobile = useSsrSafeIsMobile(true, 768)
const maxWidth = computed(() => {
  return ssrSafeIsMobile.value ? 130 : 180
})

const shareImgList = [
  'https://lp-file.lifepage.cc/img/article/mother-s-back.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/harvest.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/candle.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/father-son2.jpg?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/lotus_lantern.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/chrysanthemum.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/mother-miss-son.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/daughter-miss-father.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/father.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/son-miss-mother.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/father-miss-daughter.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/father-miss-son.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/plants-with-tears.jpg?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/wife-miss-husband.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/husband-miss-wife.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/mother-miss-daughter.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/women-s-back.png?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/couples-deep-love.jpg?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/field.webp?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/father-son.webp?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/mother2.webp?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/little-prince.webp?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/butterfly.webp?x-oss-process=image/format,jpg/resize,h_720,m_lfit',
  'https://lp-file.lifepage.cc/img/article/kid-grandpa.webp?x-oss-process=image/format,jpg/resize,h_720,m_lfit'
]

const presetThumbs: PresetThumb[] = [
  { imgUrl: shareImgList[0], name: '母亲背影' },
  { imgUrl: shareImgList[1], name: '丰收' },
  { imgUrl: shareImgList[2], name: '烛光' },
  { imgUrl: shareImgList[3], name: '父子相伴' },
  { imgUrl: shareImgList[4], name: '莲花灯' },
  { imgUrl: shareImgList[5], name: '菊花' },
  { imgUrl: shareImgList[6], name: '母念子' },
  { imgUrl: shareImgList[7], name: '女念父' },
  { imgUrl: shareImgList[8], name: '父亲' },
  { imgUrl: shareImgList[9], name: '子念母' },
  { imgUrl: shareImgList[10], name: '父念女' },
  { imgUrl: shareImgList[11], name: '父念子' },
  { imgUrl: shareImgList[12], name: '泪中绿' },
  { imgUrl: shareImgList[13], name: '妻念夫' },
  { imgUrl: shareImgList[14], name: '夫念妻' },
  { imgUrl: shareImgList[15], name: '母念女' },
  { imgUrl: shareImgList[16], name: '女子背影' },
  { imgUrl: shareImgList[17], name: '深情' },
  { imgUrl: shareImgList[18], name: '田野' },
  { imgUrl: shareImgList[19], name: '父子时光' },
  { imgUrl: shareImgList[20], name: '母亲画像' },
  { imgUrl: shareImgList[21], name: '小王子' },
  { imgUrl: shareImgList[22], name: '蝴蝶' },
  { imgUrl: shareImgList[23], name: '童与祖父' }
]
</script>

<template>
  <div
    flex="~ row"
    items-center
    justify-between
    w-full
    v-if="isObituaryHidden && privilege !== null && (privilege.role == 'creator' || privilege.role == 'admin')">
    <div w-full mr-auto ml-2 :style="{ visibility: articleList[0] && articleList[0].top ? 'visible' : 'hidden' }">
      <div flex items-center justify-start pt-1>
        <i i-radix-icons:pin-top text-third></i>
        <p px-2 text-third>{{ $t('m.marked_top') }}</p>
      </div>
    </div>
    <div ref="isHiddenRef" mr-2 v-show="isObituaryHidden">
      <RIcon
        :style="{ visibility: isObituaryHidden ? 'visible' : 'hidden' }"
        icon="i-iconamoon:menu-kebab-horizontal-fill"
        size="medium"
        @click.prevent.stop="isHiddenVisible = !isHiddenVisible"></RIcon>
      <RToolTip :wrapper-ref="isHiddenRef" placement="left" :arrow="false" v-model="isHiddenVisible">
        <div flex flex-col items-center text-base>
          <div flex="~ row" items-center @click="hideObituary()" px-3 py-2>
            <i i-mdi:show text-base></i>
            <p px-2>{{ $t('action.showObituary') }}</p>
          </div>
        </div>
      </RToolTip>
    </div>
  </div>
  <AddArticleGuide
    v-if="
      (articleList.length == 0 ||
        (articleList.length == 1 && articleList[0].obituaryDTO && articleList[0].obituaryDTO.hide == true)) &&
      canArticle
    "
    :serviceId="serviceId"
    :canArticle="canArticle"
    :canCreateObituary="canCreateObituary"
    :privilege="privilege"
    mt-2 />
  <div v-else w-full mt-2>
    <div
      v-for="(article, index) in articleList"
      relative
      w-full
      h-full
      mb-2
      rounded-3
      overflow-y-auto
      bg-card
      class="thin-scrollbar">
      <article v-if="article?.memorialArticleDTO && index == 0" w-full h-full flex="~ col" rounded-2 px-2 pt-2>
        <h1 text-9 text-main ml-2 md:ml-4 mt-3 mb-3 pr-1 font-serif-tc leading-11 font-bold>
          {{ article?.memorialArticleDTO?.title }}
        </h1>
        <Article
          :modelValue="article?.memorialArticleDTO?.content"
          :baseInfo="article?.memorialArticleDTO?.baseInfo"
          :maxHeight="props.largeMode ? 800 : 192"
          :enableExpand="true"
          :enableTheme="false"
          px-2
          md:px-4 />
        <div
          v-if="isNotEmpty(figureSummary) && props.largeMode"
          mx-2
          my-2
          bg-gray-100
          text-xs
          p-3
          rounded-2
          leading-5
          opacity-80>
          {{ t('m.public_figure_summary_block', { summary: figureSummary, fullName: fullName }) }}
        </div>
        <div flex="~ row" relative px-2 mb-4 mt-1 w-full items-center justify-between text-sm>
          <CoworkSignature
            :creatorName="article?.memorialArticleDTO?.creator?.name ? article?.memorialArticleDTO?.creator.name : null"
            :createTime="article?.memorialArticleDTO?.createdAt || 0"
            :updaterName="article?.memorialArticleDTO?.updater?.name ? article?.memorialArticleDTO?.updater.name : null"
            :updateTime="article?.memorialArticleDTO?.updatedAt || 0"
            md:ml-2
            grow />
          <div
            :ref="
              (el) => {
                if (el) moreRefs[index] = el
              }
            "
            mr-2>
            <RIcon
              v-if="canArticle"
              icon="i-iconamoon:menu-kebab-horizontal-fill"
              size="medium"
              @click.prevent.stop="toggleMore(index)"
              cursor-pointer></RIcon>
          </div>
          <RToolTip :wrapper-ref="moreRefs[index]" placement="left" :arrow="false" v-model="moreVisibles[index]">
            <div flex flex-col items-start text-main min-w-22 gap-0.5>
              <div flex items-start @click="editArticle(index)" px-3 py-3 cursor-pointer quick-menu-item>
                <i i-ri:edit-line text-base w-5 h-5></i>
                <p pl-2>{{ $t('action.edit') }}</p>
              </div>
              <div
                v-if="canDelete"
                flex="~ row"
                items-start
                px-3
                pb-2
                mt-1
                @click="handleMemorialArticlePin(article.memorialArticleDTO.id)"
                cursor-pointer
                quick-menu-item>
                <i i-radix-icons:pin-top w-5 h-5></i>
                <p pl-2>{{ article.top ? $t('action.unpin') : $t('action.pin') }}</p>
              </div>
              <div
                flex="~ row"
                w-full
                border-t-1px
                items-start
                @click="handleArticleDelete(index)"
                cursor-pointer
                text-danger
                quick-menu-item
                px-3
                py-3
                v-if="canDelete || article?.memorialArticleDTO?.creator?.id === getCurrentUser().value?.user?.id">
                <i i-mi:delete w-5 h-5></i>
                <p pl-2>{{ $t('action.delete') }}</p>
              </div>
              <div
                flex="~ row"
                items-start
                py-3
                pt-3
                border-t-1px
                @click="openContributeDialog(article.memorialArticleDTO, index)"
                cursor-pointer
                v-if="shouldShowContributeMenuItem(article.memorialArticleDTO, 200)"
                quick-menu-item>
                <i i-carbon:ibm-elo-publishing w-5 h-5></i>
                <p pl-2>
                  {{
                    article.memorialArticleDTO.publishedArticleId
                      ? $t('action.contributed')
                      : $t('action.contribute_article')
                  }}
                </p>
              </div>
            </div>
          </RToolTip>
        </div>
      </article>
      <div v-else-if="article?.obituaryDTO && index == 0" cursor-pointer rounded-3 @click="onObituaryClick(index)">
        <div class="p-2" pb-0>
          <img
            :src="showImage(article.obituaryDTO)"
            alt="讣告图片"
            aspect-fit
            w-full
            class="md:w-75% mx-auto"
            rounded-2 />
          <div flex justify-start items-center h-10 min-w-22>
            <!-- <div text-theme-dark text-main font-bold>收到花圈</div>
            <div text-second ml-1>{{ article.obituaryDTO.stat.wreathTotal }}个</div>
            <div text-theme-dark text-main font-bold ml-10>收到挽联</div>
            <div text-second ml-1>{{ article.obituaryDTO.stat.coupletTotal }}幅</div> -->
            <div
              :ref="
                (el) => {
                  if (el) moreRefs[index] = el
                }
              "
              ml-auto>
              <RIcon
                v-if="canEditObituary"
                icon="i-iconamoon:menu-kebab-horizontal-fill"
                size="medium"
                @click.prevent.stop="toggleMore(index)"></RIcon>
            </div>
            <RToolTip :wrapper-ref="moreRefs[index]" placement="left" :arrow="false" v-model="moreVisibles[index]">
              <div flex flex-col items-start text-main>
                <div flex="~ row" items-start @click="editArticle(index)" quick-menu-item px-3 py-2 cursor-pointer>
                  <i i-ri:edit-line text-base w-5 h-5></i>
                  <p pl-2>{{ $t('action.edit') }}</p>
                </div>
                <div
                  flex="~ row"
                  items-start
                  quick-menu-item
                  px-3
                  py-2
                  @click="handleObituaryPin(article.obituaryDTO.id)"
                  cursor-pointer>
                  <i i-radix-icons:pin-top text-base w-5 h-5></i>
                  <p pl-2>{{ article.top ? $t('action.unpin') : $t('action.pin') }}</p>
                </div>
                <div flex="~ row" items-start quick-menu-item px-3 py-2 @click="hideObituary(index)" cursor-pointer>
                  <i i-formkit:hidden text-base w-5 h-5></i>
                  <p pl-2>{{ $t('action.hide') }}</p>
                </div>
              </div>
            </RToolTip>
          </div>
        </div>
      </div>
      <div
        v-else-if="article?.obituaryDTO"
        w-full
        h-12
        flex
        rounded-1
        px-2
        items-center
        @click="onObituaryClick(index)">
        <h4
          text-xl
          text-main
          ml-2
          md:ml-4
          pr-1
          leading-11
          text-base
          font-bold
          line-clamp-1
          class="font-bold font-serif-tc">
          {{ $t('obituary.title') }}
        </h4>
        <div relative px-2 items-center justify-betweeen text-sm ml-auto mr-1>
          <div
            :ref="
              (el) => {
                if (el) moreRefs[index] = el
              }
            ">
            <RIcon
              v-if="canEditObituary"
              icon="i-iconamoon:menu-kebab-horizontal-fill"
              size="medium"
              @click.prevent.stop="toggleMore(index)"></RIcon>
          </div>
          <RToolTip :wrapper-ref="moreRefs[index]" placement="left" :arrow="false" v-model="moreVisibles[index]">
            <div flex flex-col items-start text-main min-w-22>
              <div flex="~ row" items-start @click="editArticle(index)" px-3 py-2 py-3 cursor-pointer>
                <i i-ri:edit-line text-base w-5 h-5></i>
                <p pl-2>{{ $t('action.edit') }}</p>
              </div>
              <div flex="~ row" items-start px-3 py-3 cursor-pointer @click="handleObituaryPin(article.obituaryDTO.id)">
                <i i-radix-icons:pin-top text-base w-5 h-5></i>
                <p pl-2>{{ article.top ? $t('action.unpin') : $t('action.pin') }}</p>
              </div>
              <div flex="~ row" items-start px-3 py-3 @click="hideObituary(index)" cursor-pointer>
                <i i-formkit:hidden text-base w-5 h-5></i>
                <p pl-2>{{ $t('action.hide') }}</p>
              </div>
            </div>
          </RToolTip>
        </div>
      </div>
      <article
        v-else
        w-full
        flex
        rounded-1
        px-2
        items-center
        @click="onArticleClick(index)"
        :class="props.largeMode ? 'flex-col' : 'flex-row'">
        <h1
          text-main
          font-bold
          md:ml-8
          pr-1
          :class="
            props.largeMode
              ? 'ml-4 w-full text-2xl leading-9 mt-2'
              : 'ml-2 text-xl text-base leading-11 font-bold line-clamp-1'
          ">
          {{ article?.memorialArticleDTO?.title }}
        </h1>
        <Article
          v-if="props.largeMode"
          :modelValue="article?.memorialArticleDTO?.content"
          :baseInfo="article?.memorialArticleDTO?.baseInfo"
          :maxHeight="props.largeMode ? 500 : 192"
          :enableExpand="true"
          :enableTheme="false"
          px-2
          p-2
          mb-2
          md:px-4 />
        <div
          relative
          px-2
          flex
          flex-row
          items-center
          justify-between
          text-sm
          ml-auto
          mr-1
          mb-2
          :class="props.largeMode ? 'w-full' : ''">
          <CoworkSignature
            v-if="props.largeMode"
            :creatorName="article?.memorialArticleDTO?.creator?.name ? article?.memorialArticleDTO?.creator.name : null"
            :createTime="article?.memorialArticleDTO?.createdAt || 0"
            :updaterName="article?.memorialArticleDTO?.updater?.name ? article?.memorialArticleDTO?.updater.name : null"
            :updateTime="article?.memorialArticleDTO?.updatedAt || 0"
            md:ml-2
            my-2
            grow />
          <div>{{ ' ' }}</div>
          <div
            :ref="
              (el) => {
                if (el) moreRefs[index] = el
              }
            ">
            <RIcon
              v-if="canArticle"
              icon="i-iconamoon:menu-kebab-horizontal-fill"
              size="medium"
              @click.prevent.stop="toggleMore(index)"></RIcon>
          </div>
          <RToolTip :wrapper-ref="moreRefs[index]" placement="left" :arrow="false" v-model="moreVisibles[index]">
            <div flex flex-col text-main min-w-28>
              <div flex="~ row" items-center @click="editArticle(index)" px-3 py-2 cursor-pointer>
                <i i-ri:edit-line text-base></i>
                <p pl-1>{{ $t('action.edit') }}</p>
              </div>
              <div
                flex="~ row"
                items-center
                px-3
                py-2
                w-full
                border-b-1px
                cursor-pointer
                @click="
                  article?.memorialArticleDTO
                    ? handleMemorialArticlePin(article.memorialArticleDTO.id)
                    : handleObituaryPin(article.obituaryDTO.id)
                ">
                <i i-radix-icons:pin-top></i>
                <p pl-1>{{ article.top ? $t('action.unpin') : $t('action.pin') }}</p>
              </div>
              <div
                flex="~ row"
                items-center
                @click="handleArticleDelete(index)"
                px-3
                py-2
                cursor-pointer
                text-danger
                v-if="canDelete || article?.memorialArticleDTO?.creator?.id === getCurrentUser().value?.user?.id">
                <i i-mi:delete></i>
                <p pl-1>{{ $t('action.delete') }}</p>
              </div>
              <div
                flex="~ row"
                items-start
                py-3
                pt-3
                border-t-1px
                @click="openContributeDialog(article.memorialArticleDTO, index)"
                cursor-pointer
                v-if="shouldShowContributeMenuItem(article.memorialArticleDTO, 200)"
                quick-menu-item>
                <i i-carbon:ibm-elo-publishing w-5 h-5></i>
                <p pl-2>
                  {{
                    article.memorialArticleDTO.publishedArticleId
                      ? $t('action.contributed')
                      : $t('action.contribute_article')
                  }}
                </p>
              </div>
            </div>
          </RToolTip>
        </div>
      </article>
    </div>
    <CreateArticleButton v-if="canArticle" :canCreateObituary="canCreateObituary" :privilege="privilege" />

    <!-- <button v-if="canArticle" ref="newArticleRef" flex items-center justify-center btn-theme-outline h-10 min-w-26 mt-4 mx-auto
      @click="handleCreateArticle">
      <p class="i-mynaui:plus" w-6 h-6 mr-1 ml--1></p>
      <p>{{ $t('action.article') }}</p>
    </button>

    <RToolTip :wrapper-ref="newArticleRef" placement="bottom" :arrow="true" v-model="newArticle">
      <div flex flex-col items-center text-base>
        <div flex="~ row" items-center justify-start px-3 pb-2 border-b-1 w-full @click="createArticle" cursor-pointer>
          <i i-bx:edit text-base></i>
          <p px-2>{{ $t('privilege.item_m_article') }}</p>
        </div>
        <div v-if="canCreateObituary" flex="~ row" items-center justify-start px-3 pt-2 w-full @click="createObituary"
          cursor-pointer>
          <i i-bx:edit></i>
          <p px-2>{{ $t('obituary.title') }}</p>
        </div>
      </div>
    </RToolTip> -->
  </div>

  <RAction
    v-model="showArticleDrawer"
    :fromBottom="true"
    :mask="true"
    :expandable="true"
    :maxWidth="maxWidth"
    scrollable
    heightQuarter
    backdropHide>
    <div w-full h-full flex="~ col" px-4 pt-2 bg-base overflow-y-auto>
      <div w-full h-14 flex items-center justify-between px-2 md:px-4 mt-2 cursor-pointer>
        <p bg-tag-base bg-m-light text-sm px-2 py-1 rounded>{{ $t('m.tag_m_artile') }}</p>
        <div class="i-iconamoon:arrow-down-2-light" text-2xl text-third @click="showArticleDrawer = false"></div>
      </div>
      <h1 text-9 text-main ml-2 md:ml-4 mt-3 mb-3 pr-1 leading-11 font-bold class="font-bold font-serif-tc">
        {{ currentArticle?.title }}
      </h1>
      <Article
        :modelValue="currentArticle?.content"
        :baseInfo="currentArticle?.baseInfo"
        :enableExpand="false"
        :enableTheme="false"
        px-2
        md:px-4
        :overflowHidden="false"
        :maxHeight="200" />
      <div flex="~ row" relative px-2 mb-10 mt-4 w-full items-center justify-betweeen text-sm>
        <CoworkSignature
          v-if="currentArticle"
          :creatorName="currentArticle?.creator?.name ? currentArticle?.creator?.name : null"
          :createTime="currentArticle?.createdAt || 0"
          :updaterName="currentArticle?.updater?.name ? currentArticle?.updater?.name : null"
          :updateTime="currentArticle?.updatedAt || 0"
          md:ml-2
          grow />
        <div ref="currentArticleRef" mr-2>
          <RIcon
            v-if="canArticle"
            icon="i-iconamoon:menu-kebab-horizontal-fill"
            size="medium"
            @click.prevent.stop="moreVisible = !moreVisible"
            cursor-pointer></RIcon>
          <RToolTip :wrapper-ref="currentArticleRef" placement="bottom" :arrow="true" v-model="moreVisible">
            <div flex flex-col items-center text-base p-2>
              <div
                flex="~ row"
                items-center
                @click="editArticle(currentArticleIndex)"
                px-3
                pb-2
                border-b-1
                cursor-pointer>
                <i i-ri:edit-line text-base></i>
                <p px-2>{{ $t('action.edit') }}</p>
              </div>
              <div
                flex="~ row"
                items-center
                px-3
                py-2
                border-b-1
                @click="handleArticleDelete(currentArticleIndex)"
                cursor-pointer>
                <i i-mi:delete></i>
                <p px-2>{{ $t('action.delete') }}</p>
              </div>
              <div
                flex="~ row"
                items-center
                px-3
                pt-2
                @click="handleMemorialArticlePin(currentArticle?.id)"
                cursor-pointer>
                <i i-radix-icons:pin-top text-base></i>
                <p px-2>{{ currentArticle.top ? $t('action.unpin') : $t('action.pin') }}</p>
              </div>
            </div>
          </RToolTip>
        </div>
      </div>
    </div>
  </RAction>
  <el-dialog
    v-model="contributeDialogVisible"
    :fullscreen="ssrSafeIsMobile"
    width="720px"
    :z-index="5000"
    style="max-width: 95%; margin-top: 30px; padding-left: 5px; padding-right: 5px"
    :show-close="false"
    destroy-on-close>
    <div w-full flex flex-col>
      <div relative w-full flex items-center justify-center pb-4>
        <div text-2xl font-bold>{{ t('article.contribute_dialog_title') }}</div>
        <div
          i-material-symbols-light:close
          absolute
          right-4
          top-0
          p-2
          icon-button
          text-2xl
          text-third
          @click="contributeDialogVisible = false" />
      </div>
      <div px-6 pb-6 of-y-auto>
        <div border-1 border-yellow-200 bg-yellow-50 rounded-2 p-4 text-main leading-7>
          {{ t('article.contribute_tip') }}
        </div>
        <div mt-6>
          <RInput v-model="contributeTitle" maxlength="100" :placeholder="t('article.contribute_title_label')" />
        </div>
        <div mt-6>
          <RInput v-model="contributeAuthor" maxlength="20" :placeholder="t('article.contribute_author_label')" />
        </div>
        <div mt-4 flex items-baseline>
          <div text-base font-bold>{{ t('article.contribute_intro_label') }}</div>
          <div text-xs text-third ml-2>{{ t('article.contribute_intro_min_hint', { count: 10 }) }}</div>
        </div>
        <div mt-2>
          <textarea
            v-model="contributeIntro"
            w-full
            rounded-2
            input-base
            p-4
            min-h-32
            maxlength="80"
            style="resize: none"
            class="bg-gray-50" />
        </div>

        <div mt-4 flex items-center justify-between>
          <div text-xl font-bold>{{ t('article.contribute_thumbnail_label') }}</div>
          <div flex items-center gap-2>
            <button type="button" btn-outline class="bg-gray-100" @click="triggerThumbUpload">
              {{ t('article.contribute_upload_thumbnail') }}
            </button>
            <button type="button" btn-outline class="bg-gray-100" @click="openThumbPicker">
              {{ t('article.contribute_choose_from_gallery') }}
            </button>
          </div>
        </div>
        <div mt-2 mb-20>
          <input ref="thumbFileInputRef" type="file" accept="image/*" class="hidden" @change="onThumbFileSelected" />
          <div
            w-28
            h-28
            rounded-2
            bg-gray-100
            flex
            items-center
            justify-center
            overflow-hidden
            cursor-pointer
            relative
            @click="triggerThumbUpload">
            <img v-if="contributeThumbUrl" :src="contributeThumbUrl" class="w-full h-full object-cover" />
            <div v-else i-mynaui:plus text-5xl text-third />
            <button
              v-if="contributeThumbUrl"
              type="button"
              class="absolute top-1 right-1 bg-black/40 hover:bg-black/55 text-inverted rounded-full p-1"
              @click.stop="removeContributeThumb">
              <span i-material-symbols-light:delete w-4.5 h-4.5></span>
            </button>
          </div>
        </div>
      </div>

      <div absolute bottom-2 w-full px-6 pb-4 pt-4>
        <button type="button" w-full btn-solid py-4 text-lg :disabled="contributeSubmitting" @click="submitContribute">
          {{ t('article.contribute_submit') }}
        </button>
      </div>
    </div>
  </el-dialog>

  <el-dialog
    v-model="thumbPickerVisible"
    :fullscreen="ssrSafeIsMobile"
    width="750px"
    :z-index="5000"
    style="max-width: 95%"
    :show-close="false"
    destroy-on-close>
    <div w-full flex flex-col>
      <div relative w-full flex items-center justify-center py-4>
        <div text-xl font-bold>{{ t('article.contribute_select_thumbnail_title') }}</div>
        <div
          i-material-symbols-light:close
          absolute
          right-2
          top-2
          p-2
          icon-button
          text-2xl
          text-third
          @click="thumbPickerVisible = false" />
      </div>

      <div px-6 pb-6>
        <div v-if="presetThumbs.length > 0" grid="~ cols-3 md:cols-5" gap-4>
          <button
            v-for="item in presetThumbs"
            :key="item.imgUrl"
            type="button"
            class="text-left rounded-2 bg-grey"
            @click="onPresetThumbClick(item.imgUrl)">
            <div
              w-full
              aspect-square
              rounded-2
              overflow-hidden
              :class="contributeThumbUrl === item.imgUrl ? 'border-primary' : 'border-transparent'">
              <img :src="item.imgUrl" class="w-full h-full object-cover" />
            </div>
            <div my-2 text-sm text-main text-center truncate>
              {{ item.name }}
            </div>
          </button>
        </div>
        <div v-else flex items-center justify-center py-12 text-third>{{ t('alt.no_data') }}</div>
      </div>
    </div>
  </el-dialog>
  <el-dialog
    v-model="contributeThankVisible"
    width="380px"
    :z-index="5100"
    :show-close="false"
    align-center
    style="max-width: 90vw"
    destroy-on-close>
    <div flex flex-col items-center text-center class="px-4 py-2">
      <div class="w-18 h-18 rounded-full bg-[#ff5a79]/10 flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 64 64">
          <path
            fill="#ff5a79"
            d="M61.1 18.2c-6.4-17-27.2-9.4-29.1-.9c-2.6-9-22.9-15.7-29.1.9C-4 36.7 29.6 53.3 32 56c2.4-2.2 36-19.6 29.1-37.8" />
        </svg>
      </div>
      <div class="mt-4 text-xl font-bold text-main">{{ t('article.contribute_thanks_title') }}</div>
      <div class="mt-2 text-sm text-third leading-6">{{ t('article.contribute_thanks_message') }}</div>
      <button type="button" class="mt-6 w-44 btn-solid py-2" @click="contributeThankVisible = false">
        {{ t('action.ok') }}
      </button>
    </div>
  </el-dialog>
  <SetRealNameDialog ref="realNameModalRef" />
  <ClientOnly>
    <teleport to="body">
      <ImageCropperPage
        v-model="thumbCropVisible"
        :path="thumbCropPath"
        fixed
        highQuality
        @onCropped="onThumbCropResult"
        class="fixed top-0 bottom-0 left-0 right-0 z-50 h-screen w-full" />
    </teleport>
  </ClientOnly>
</template>
<style scoped>
/* style */
</style>
